import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const EnterpriseContact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    jobTitle: '',
    companySize: '',
    monthlyMessages: '',
    useCase: '',
    timeline: '',
    additionalRequirements: '',
    agreeToTerms: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const companySizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-1000', label: '201-1000 employees' },
    { value: '1000+', label: '1000+ employees' }
  ];

  const timelineOptions = [
    { value: 'immediate', label: 'Immediate (within 1 month)' },
    { value: '1-3months', label: '1-3 months' },
    { value: '3-6months', label: '3-6 months' },
    { value: '6months+', label: '6+ months' },
    { value: 'evaluating', label: 'Just evaluating options' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Invalid email format';
    if (!formData?.company?.trim()) newErrors.company = 'Company name is required';
    if (!formData?.companySize) newErrors.companySize = 'Company size is required';
    if (!formData?.monthlyMessages?.trim()) newErrors.monthlyMessages = 'Expected message volume is required';
    if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Enterprise contact form submitted:', formData);
      setIsSubmitting(false);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        jobTitle: '',
        companySize: '',
        monthlyMessages: '',
        useCase: '',
        timeline: '',
        additionalRequirements: '',
        agreeToTerms: false
      });
      
      alert('Thank you! Our enterprise team will contact you within 24 hours.');
    }, 2000);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
            <Icon name="Building2" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-foreground">Enterprise Solutions</h3>
            <p className="text-muted-foreground">Get a custom quote tailored to your needs</p>
          </div>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <h4 className="text-lg font-semibold text-foreground mb-4">Enterprise Benefits</h4>
            <div className="space-y-3">
              {[
                'Custom pricing based on volume',
                'Dedicated account manager',
                'Priority support with SLA',
                'Custom integrations & features',
                'On-premise deployment options',
                'Advanced security & compliance',
                'Custom data retention policies',
                'White-label solutions available'
              ]?.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <Icon name="Check" size={16} className="text-accent flex-shrink-0" />
                  <span className="text-sm text-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 bg-accent/5 border border-accent/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Clock" size={20} className="text-accent mt-0.5 flex-shrink-0" />
              <div>
                <h5 className="font-medium text-foreground mb-1">Quick Response</h5>
                <p className="text-sm text-muted-foreground">
                  Our enterprise team typically responds within 4 hours during business hours. 
                  For urgent requests, call us directly at +1 (555) 123-4567.
                </p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="First Name"
              type="text"
              required
              value={formData?.firstName}
              onChange={(e) => handleInputChange('firstName', e?.target?.value)}
              error={errors?.firstName}
              placeholder="John"
            />
            <Input
              label="Last Name"
              type="text"
              required
              value={formData?.lastName}
              onChange={(e) => handleInputChange('lastName', e?.target?.value)}
              error={errors?.lastName}
              placeholder="Doe"
            />
          </div>

          <Input
            label="Work Email"
            type="email"
            required
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            placeholder="john.doe@company.com"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Company Name"
              type="text"
              required
              value={formData?.company}
              onChange={(e) => handleInputChange('company', e?.target?.value)}
              error={errors?.company}
              placeholder="Acme Corp"
            />
            <Input
              label="Job Title"
              type="text"
              value={formData?.jobTitle}
              onChange={(e) => handleInputChange('jobTitle', e?.target?.value)}
              placeholder="CTO"
            />
          </div>

          <Select
            label="Company Size"
            required
            options={companySizeOptions}
            value={formData?.companySize}
            onChange={(value) => handleInputChange('companySize', value)}
            error={errors?.companySize}
            placeholder="Select company size"
          />

          <Input
            label="Expected Monthly Messages"
            type="text"
            required
            value={formData?.monthlyMessages}
            onChange={(e) => handleInputChange('monthlyMessages', e?.target?.value)}
            error={errors?.monthlyMessages}
            placeholder="e.g., 10 million"
            description="Approximate volume you expect to process"
          />

          <Input
            label="Primary Use Case"
            type="text"
            value={formData?.useCase}
            onChange={(e) => handleInputChange('useCase', e?.target?.value)}
            placeholder="e.g., Customer support chat, In-app messaging"
            description="How will you use our messaging platform?"
          />

          <Select
            label="Implementation Timeline"
            options={timelineOptions}
            value={formData?.timeline}
            onChange={(value) => handleInputChange('timeline', value)}
            placeholder="Select timeline"
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Additional Requirements
            </label>
            <textarea
              value={formData?.additionalRequirements}
              onChange={(e) => handleInputChange('additionalRequirements', e?.target?.value)}
              placeholder="Tell us about any specific requirements, compliance needs, or custom features you need..."
              rows={4}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            />
          </div>

          <Checkbox
            label="I agree to be contacted by MessagingHub Pro regarding enterprise solutions"
            checked={formData?.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e?.target?.checked)}
            error={errors?.agreeToTerms}
            required
          />

          <Button
            type="submit"
            variant="default"
            size="lg"
            fullWidth
            loading={isSubmitting}
            iconName="Send"
            iconPosition="right"
            disabled={isSubmitting}
            className="shadow-elevation-2"
          >
            {isSubmitting ? 'Submitting Request...' : 'Request Enterprise Quote'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default EnterpriseContact;